import { useEffect, useId, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertCircle, LayoutTemplate, Loader2, Save, Unlink, X } from 'lucide-react'
import { useBuilderPages } from './hooks/useBuilderPages'
import { BuilderPageAutocomplete } from './BuilderPageAutocomplete'
import { ModelPathField } from './ModelPathField'
import { defaultModelPath, fieldsOf, validateModelPath } from './modelPath'

/**
 * Onglet « Modèle » d'un type de contenu — la page qui en rend les contenus.
 *
 * 1 type = 1 modèle. Ce qui se règle ici :
 *  - QUELLE page est le modèle — créée à partir du gabarit par défaut du site,
 *    d'une page du site (copie) ou de rien ;
 *  - le CHEMIN public des contenus, construit avec leurs champs
 *    (`/actualites/@titre`) — le même champ (`ModelPathField`) que le panneau
 *    « Adresse » ouvert depuis le modèle dans l'éditeur de blocs, qui écrit par
 *    le même endpoint ;
 *  - la CORRESPONDANCE des champs (avancé : un bloc de modèle lit
 *    `~~._context_.title` quand ce type appelle son titre `name`).
 * Les BLOCS du modèle, eux, s'éditent dans le builder (« Ouvrir l'éditeur de
 * blocs ») — une page comme une autre, qui porte en plus la marque `modelFor`.
 *
 * Toutes les écritures passent par POST /api/builder/object-def/model, qui
 * tient la règle côté serveur ; cet écran la rend lisible, et MONTRE les
 * conflits hérités (plusieurs modèles, modèle marqué gabarit) au lieu d'en
 * trancher un en silence.
 *
 * Pourquoi React et pas un rjBuild : l'éditeur de correspondance manipule un
 * objet à CLÉS DYNAMIQUES (`{cléDeContexte: champ}`, lignes ajoutées et
 * retirées, options tirées du schéma YAML du type), que les champs de
 * formulaire reactive-json — liés à des chemins fixes — ne savent pas éditer ;
 * et le choix de la page de départ réutilise BuilderPageAutocomplete (recherche
 * serveur bornée), un composant React sans équivalent déclaratif.
 *
 * La création enchaîne deux appels (créer ou dupliquer la page, PUIS la
 * désigner) : ce n'est pas atomique. Si le second échoue, la page existe déjà —
 * l'écran le dit et RETENTE la désignation sur cette même page au prochain
 * envoi, au lieu d'en créer une seconde.
 */

const MODEL_ENDPOINT = '/api/builder/object-def/model'

/**
 * Appelle l'endpoint du modèle. Un refus (409 d'un conflit, 403…) devient une
 * erreur PORTEUSE du message du back : c'est lui qui sait dire « ce type a
 * déjà un modèle (« X ») ».
 */
const postModel = async (body) => {
  const res = await fetch(MODEL_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok || !data.success) {
    const error = new Error(data.message || `Erreur ${res.status}`)
    error.reason = data.reason ?? null
    throw error
  }
  return data
}

/** Retire les lignes incomplètes : une clé sans champ ne mappe rien. */
const cleanMapping = (mapping) => Object.fromEntries(
  Object.entries(mapping || {}).filter(([contextKey, objectKey]) => contextKey && objectKey),
)

export const BuilderTypeModelTab = ({ type, models, loading, onChanged }) => {
  // Le spinner seulement au PREMIER chargement : pendant une relecture (après
  // un enregistrement), la carte reste montée — et avec elle son message.
  if (loading && models.length === 0) {
    return (
      <div className="flex items-center justify-center py-16" role="status">
        <span className="spinner" aria-hidden="true" />
        <span className="sr-only">Chargement du modèle…</span>
      </div>
    )
  }
  if (models.length === 0) {
    return <CreateModel type={type} onCreated={onChanged} />
  }
  return (
    <div className="space-y-4">
      {models.length > 1 && (
        <div role="alert" className="alert alert-warning items-start">
          <AlertCircle size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
          <p>
            Ce type a {models.length} modèles — un héritage d&apos;avant la règle « un type,
            un modèle ». Une seule page peut servir les adresses de ses contenus :
            détachez les autres.
          </p>
        </div>
      )}
      {models.map(model => (
        <ModelCard key={model.slug} type={type} model={model} onChanged={onChanged} />
      ))}
    </div>
  )
}

/** Le modèle posé : chemin, correspondance, éditeur de blocs, détachement. */
const ModelCard = ({ type, model, onChanged }) => {
  const headingId = useId()
  const pathId = useId()
  const [path, setPath] = useState(model.path || defaultModelPath(type.name, type.definition))
  const [mapping, setMapping] = useState(() => model.mapping ?? {})
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState(null) // {kind: 'ok'|'error', text}
  const fields = useMemo(() => fieldsOf(type.definition), [type.definition])

  // Le serveur a pu retenir autre chose que la saisie (chemin normalisé) : on
  // repart de SA version à chaque relecture.
  useEffect(() => {
    setPath(model.path || defaultModelPath(type.name, type.definition))
    setMapping(model.mapping ?? {})
  }, [model.path, model.mapping, type.name, type.definition])

  const pathValid = validateModelPath(path, fields.map(f => f.key)) === null
  const dirty = path !== (model.path || '')
    || JSON.stringify(cleanMapping(mapping)) !== JSON.stringify(cleanMapping(model.mapping))

  const run = async (body, okText) => {
    setBusy(true)
    setMessage(null)
    try {
      await postModel(body)
      setMessage({ kind: 'ok', text: okText })
      onChanged?.()
    } catch (e) {
      setMessage({ kind: 'error', text: e.message })
    } finally {
      setBusy(false)
    }
  }

  const save = () => run(
    { type: type.type, page: model.slug, path, mapping: cleanMapping(mapping) },
    'Modèle enregistré.',
  )
  const detach = () => {
    const label = model.title || model.slug
    // Détacher rend la page à la vie publique à SON adresse : un geste qui
    // change ce que voient les visiteurs mérite une confirmation.
    if (!window.confirm(`Détacher « ${label} » ? Elle redevient une page ordinaire, servie à sa propre adresse, et les contenus de ce type n'auront plus de vue publique tant qu'aucun modèle n'est posé.`)) return
    run({ type: type.type, page: model.slug, detach: true }, 'Modèle détaché.')
  }
  const promote = () => run(
    { type: type.type, page: model.slug, path, mapping: cleanMapping(mapping), clearTemplate: true },
    "La page n'est plus un template : elle sert désormais les contenus de ce type.",
  )
  return (
    <section aria-labelledby={headingId} className="bg-white border border-gray-200 rounded-xl">
      <header className="px-5 py-4 border-b border-gray-100 flex flex-wrap items-center gap-3">
        <div className="min-w-0">
          <h2 id={headingId} className="text-sm font-semibold text-gray-800 truncate">{model.title || model.slug}</h2>
          <p className="text-xs font-mono text-gray-500 truncate">/builder/{model.slug}</p>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Link to={`/builder/${model.slug}`} className="btn btn-primary btn-sm">
            <LayoutTemplate size={14} aria-hidden="true" />
            Ouvrir l&apos;éditeur de blocs
          </Link>
          <button type="button" onClick={detach} disabled={busy} className="btn btn-secondary btn-sm">
            <Unlink size={14} aria-hidden="true" />
            Détacher
          </button>
        </div>
      </header>

      {model.isTemplate && (
        <div role="alert" className="alert alert-warning mx-5 mt-4 flex-wrap items-start">
          <AlertCircle size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
          <p className="flex-1 min-w-[16rem]">
            Cette page est marquée <strong>template</strong> : un template n&apos;est servi
            nulle part, donc les contenus de ce type n&apos;ont aucune adresse publique.
          </p>
          <button type="button" onClick={promote} disabled={busy || !pathValid} className="btn btn-primary btn-sm">
            En faire le modèle servi
          </button>
        </div>
      )}

      <div className="px-5 py-4 space-y-4">
        <ModelPathField
          id={pathId}
          value={path}
          onChange={setPath}
          fields={fields}
          siteName={type.siteName || ''}
        />

        <MappingEditor fields={fields} mapping={mapping} onChange={setMapping} />

        <div className="flex flex-wrap items-center gap-3">
          <button type="button" onClick={save} disabled={!dirty || !pathValid || busy} className="btn btn-primary btn-sm">
            {busy
              ? <Loader2 size={14} className="animate-spin" aria-hidden="true" />
              : <Save size={14} aria-hidden="true" />}
            Enregistrer
          </button>
          {message && (
            <p
              role={message.kind === 'error' ? 'alert' : 'status'}
              className={`alert ${message.kind === 'error' ? 'alert-danger' : 'alert-success'} py-1.5`}
            >
              {message.text}
            </p>
          )}
        </div>
      </div>
    </section>
  )
}

/**
 * Correspondance `{cléDeContexte: champDuType}` — AVANCÉ. Vide = chaque champ du
 * type est lu tel quel sous `~~._context_.<clé>` ; une ligne renomme (le bloc lit
 * `title`, le type l'appelle `name`). Repliée tant qu'elle est vide : un modèle
 * fait pour son type n'en a pas besoin.
 */
const MappingEditor = ({ fields, mapping, onChange }) => {
  const newKeyId = useId()
  const [draftKey, setDraftKey] = useState('')
  const entries = Object.entries(mapping || {})

  const setRow = (contextKey, objectKey) => onChange({ ...mapping, [contextKey]: objectKey })
  const removeRow = (contextKey) => {
    const next = { ...mapping }
    delete next[contextKey]
    onChange(next)
  }
  const addRow = () => {
    const key = draftKey.trim()
    if (!key || key in (mapping || {})) return
    onChange({ ...mapping, [key]: fields[0]?.key ?? '' })
    setDraftKey('')
  }

  return (
    <details className="rounded-lg border border-gray-100 bg-gray-50/60" open={entries.length > 0}>
      <summary className="px-3 py-2 text-xs font-medium text-gray-600 cursor-pointer">
        Correspondance des champs (avancé){entries.length > 0 ? ` — ${entries.length}` : ''}
      </summary>
      <div className="px-3 pb-3 space-y-2">
        <p className="text-xs text-gray-500">
          Vide : chaque champ du type est lu par les blocs sous{' '}
          <code className="font-mono">~~._context_.&lt;clé&gt;</code>. Une ligne renomme —
          utile quand un bloc de modèle lit <code className="font-mono">title</code> et
          que ce type appelle son titre autrement.
        </p>
        {entries.map(([contextKey, objectKey]) => (
          <div key={contextKey} className="flex items-center gap-2">
            <span className="text-xs font-mono text-gray-700 min-w-[8rem]">_context_.{contextKey}</span>
            <span aria-hidden="true" className="text-xs text-gray-400">←</span>
            <label htmlFor={`${newKeyId}-${contextKey}`} className="sr-only">
              Champ du type lu sous _context_.{contextKey}
            </label>
            <select
              id={`${newKeyId}-${contextKey}`}
              value={objectKey}
              onChange={e => setRow(contextKey, e.target.value)}
              className="input flex-1 font-mono text-xs"
            >
              {fields.length === 0 && <option value="">—</option>}
              {fields.map(f => (
                <option key={f.key} value={f.key}>{f.label ? `${f.label} (${f.key})` : f.key}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => removeRow(contextKey)}
              className="btn btn-ghost btn-sm px-2"
              aria-label={`Retirer la correspondance ${contextKey}`}
            >
              <X size={12} aria-hidden="true" />
            </button>
          </div>
        ))}
        <div className="flex items-center gap-2">
          <label htmlFor={newKeyId} className="sr-only">Clé lue par les blocs</label>
          <input
            id={newKeyId}
            type="text"
            value={draftKey}
            onChange={e => setDraftKey(e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addRow() } }}
            placeholder="clé lue par les blocs"
            className="input flex-1 font-mono text-xs"
          />
          <button
            type="button"
            onClick={addRow}
            disabled={!draftKey.trim() || fields.length === 0}
            className="btn btn-secondary btn-sm"
          >
            Ajouter
          </button>
        </div>
      </div>
    </details>
  )
}

/**
 * Pas encore de modèle : le poser. La page naît sur le site du TYPE — un modèle
 * porte l'habillage de son site —, à partir du gabarit par défaut du site
 * (présélectionné quand il existe), d'une page du site ou de rien, puis est
 * désignée comme modèle.
 */
const CreateModel = ({ type, onCreated }) => {
  const headingId = useId()
  const titleFieldId = useId()
  const pathId = useId()
  const sourceId = useId()
  const sourceHelpId = useId()
  const originName = useId()
  // Les pages et gabarits DU SITE du type : `templates` résout le gabarit par
  // défaut (un nid) en slug, le seul identifiant que la duplication connaît.
  const { createPage, duplicatePage, templates } = useBuilderPages(type.siteId)
  const [defaultModelNid, setDefaultModelNid] = useState(null)
  const [title, setTitle] = useState(`Modèle — ${type.name}`)
  const [path, setPath] = useState(defaultModelPath(type.name, type.definition))
  const fields = useMemo(() => fieldsOf(type.definition), [type.definition])
  // `null` = pas encore choisi : le gabarit par défaut l'emporte s'il existe.
  const [origin, setOrigin] = useState(null)
  const [sourceSlug, setSourceSlug] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  // La page déjà créée dont la DÉSIGNATION a échoué : le prochain envoi la
  // désigne au lieu d'en créer une autre (les deux appels ne sont pas atomiques).
  const [pendingSlug, setPendingSlug] = useState(null)

  // Le gabarit par défaut du site (`defaultModelNid`, entier calculé par
  // orbit_site). Enveloppe Syncable pour qui peut écrire le site, objet plat
  // sinon — même piège que BuilderCreateModal.
  useEffect(() => {
    if (!type.siteId) return undefined
    let cancelled = false
    fetch(`/decoupled-api/group/site/${type.siteId}?display=decoupled`, { credentials: 'include' })
      .then(res => (res.ok ? res.json() : null))
      .then((payload) => {
        if (cancelled || !payload) return
        const site = payload.data ?? payload
        setDefaultModelNid(site?.defaultModelNid ?? null)
      })
      .catch(() => { /* le gabarit par défaut est un confort */ })
    return () => { cancelled = true }
  }, [type.siteId])

  const defaultTemplate = useMemo(
    () => (defaultModelNid ? templates.find(t => t.nid === defaultModelNid) ?? null : null),
    [templates, defaultModelNid],
  )
  const effectiveOrigin = origin ?? (defaultTemplate ? 'default' : 'other')
  const pathValid = validateModelPath(path, fields.map(f => f.key)) === null

  if (!type.siteId) {
    return (
      <div role="note" className="alert alert-warning items-start">
        <AlertCircle size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
        <p>
          Ce type n&apos;est rattaché à aucun site. Un modèle appartient au site dont il
          porte l&apos;habillage : rattachez d&apos;abord le type à un site, dans l&apos;onglet{' '}
          <Link to={`/builder/types/${type.type}/schema`} className="underline">Schéma</Link>.
        </p>
      </div>
    )
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const trimmed = title.trim()
    if (!trimmed || !pathValid) return
    setBusy(true)
    setError(null)

    let slug = pendingSlug
    if (!slug) {
      const startSlug = effectiveOrigin === 'default' ? (defaultTemplate?.slug ?? '') : sourceSlug
      try {
        // La copie d'un gabarit ou d'une page naît page ORDINAIRE (jamais
        // gabarit, jamais modèle : `duplicate()` ne recopie pas `modelFor`) —
        // c'est l'appel suivant qui la désigne.
        slug = startSlug
          ? await duplicatePage(startSlug, trimmed, type.siteId, false)
          : await createPage(trimmed, [], type.siteId, false)
      } catch (err) {
        setError(err?.message || "La page modèle n'a pas pu être créée.")
        setBusy(false)
        return
      }
    }
    try {
      await postModel({ type: type.type, page: slug, path })
      onCreated?.()
    } catch (err) {
      setPendingSlug(slug)
      setError(`La page /builder/${slug} a été créée, mais n'a pas pu devenir le modèle : ${err.message} Corrigez si besoin, puis relancez — c'est cette même page qui sera désignée, aucune autre ne sera créée.`)
      setBusy(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} aria-labelledby={headingId} className="bg-white border border-gray-200 rounded-xl">
      <header className="px-5 py-4 border-b border-gray-100">
        <h2 id={headingId} className="text-sm font-semibold text-gray-800">Poser le modèle de ce type</h2>
        <p className="text-xs text-gray-500 mt-1">
          Le modèle est la page qui affiche chaque contenu de ce type à son adresse
          publique{type.siteName ? <>, sur le site {type.siteName}</> : null}. Ses blocs
          lisent les champs du contenu ; ils se modifient ensuite dans l&apos;éditeur de blocs.
        </p>
      </header>

      <div className="px-5 py-4 space-y-4">
        <div>
          <label htmlFor={titleFieldId} className="field-label">Titre de la page modèle</label>
          <input
            id={titleFieldId}
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            required
            disabled={!!pendingSlug}
            className="input w-full"
          />
        </div>

        <ModelPathField
          id={pathId}
          value={path}
          onChange={setPath}
          fields={fields}
          siteName={type.siteName || ''}
        />

        <fieldset className="space-y-2" disabled={!!pendingSlug}>
          <legend className="field-label">Point de départ</legend>
          {defaultTemplate && (
            <>
              <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="radio"
                  name={originName}
                  value="default"
                  checked={effectiveOrigin === 'default'}
                  onChange={() => setOrigin('default')}
                  className="mt-1"
                />
                <span>
                  Le template par défaut du site — <strong>{defaultTemplate.title || defaultTemplate.slug}</strong>
                  <span className="block text-xs text-gray-500">
                    Le modèle en reprend les blocs : l&apos;habillage du site (header, footer) est déjà là.
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="radio"
                  name={originName}
                  value="other"
                  checked={effectiveOrigin === 'other'}
                  onChange={() => setOrigin('other')}
                  className="mt-1"
                />
                <span>Une page du site, ou une page vierge</span>
              </label>
            </>
          )}
          {effectiveOrigin === 'other' && (
            <div className={defaultTemplate ? 'pl-6' : ''}>
              <label htmlFor={sourceId} className="sr-only">Page de départ</label>
              <BuilderPageAutocomplete
                id={sourceId}
                siteId={type.siteId}
                value={sourceSlug}
                onChange={setSourceSlug}
                describedById={sourceHelpId}
              />
              <p id={sourceHelpId} className="text-xs text-gray-500 mt-1">
                Les blocs de la page choisie sont COPIÉS dans le modèle : la page
                d&apos;origine n&apos;est pas touchée.
              </p>
            </div>
          )}
        </fieldset>

        {error && <p role="alert" className="alert alert-danger">{error}</p>}

        <div className="flex justify-end">
          <button type="submit" disabled={busy || !title.trim() || !pathValid} className="btn btn-primary btn-sm">
            {busy && <Loader2 size={14} className="animate-spin" aria-hidden="true" />}
            {pendingSlug ? 'Désigner la page créée comme modèle' : 'Créer le modèle'}
          </button>
        </div>
      </div>
    </form>
  )
}
